import { useEffect, useMemo, useRef, useState } from 'react';
import { AccessibilityInfo, Animated, Easing } from 'react-native';

export default function usePetMotion({ isVisible = true, isHovered = false, isWorking = false } = {}) {
  const bob = useRef(new Animated.Value(0)).current;
  const hover = useRef(new Animated.Value(0)).current;
  const [reduceMotion, setReduceMotion] = useState(true);

  useEffect(() => {
    let mounted = true;
    AccessibilityInfo.isReduceMotionEnabled().then(value => {
      if (mounted) setReduceMotion(value);
    });
    const subscription = AccessibilityInfo.addEventListener?.('reduceMotionChanged', setReduceMotion);
    return () => {
      mounted = false;
      subscription?.remove?.();
    };
  }, []);

  useEffect(() => {
    bob.stopAnimation();
    if (!isVisible || reduceMotion) {
      bob.setValue(0);
      return undefined;
    }

    const timing = (toValue, duration) => Animated.timing(bob, {
      toValue,
      duration,
      easing: Easing.inOut(Easing.sin),
      useNativeDriver: true,
      isInteraction: false,
    });
    const duration = isWorking ? 650 : 1800;
    const animation = isHovered
      ? timing(0, 180)
      : Animated.loop(Animated.sequence([timing(1, duration), timing(0, duration)]));
    animation.start();
    return () => animation.stop();
  }, [bob, isHovered, isVisible, isWorking, reduceMotion]);

  useEffect(() => {
    hover.stopAnimation();
    if (!isVisible || reduceMotion) {
      hover.setValue(0);
      return undefined;
    }

    // Greet once and hold still until the pointer leaves; never loop hover.
    const animation = Animated.timing(hover, {
      toValue: isHovered ? 1 : 0,
      duration: 180,
      easing: Easing.out(Easing.quad),
      useNativeDriver: true,
      isInteraction: false,
    });
    animation.start();
    return () => animation.stop();
  }, [hover, isHovered, isVisible, reduceMotion]);

  // Apply this only to non-interactive artwork inside a stationary button.
  // macOS re-centers scaled/rotated RCTView layers during display, which can
  // compound into drift. Pure translation avoids that adjustment entirely.
  const animatedStyle = useMemo(() => reduceMotion ? null : {
    transform: [{
      translateY: Animated.add(
        bob.interpolate({
          inputRange: [0, 1],
          outputRange: [0, isWorking ? -4 : -3],
          extrapolate: 'clamp',
        }),
        hover.interpolate({
          inputRange: [0, 1],
          outputRange: [0, -4],
          extrapolate: 'clamp',
        })
      ),
    }],
  }, [bob, hover, isWorking, reduceMotion]);

  return { animatedStyle, reduceMotion };
}
